import { zodResolver } from "@hookform/resolvers/zod";
import { SubmitHandler, useForm } from "react-hook-form";

import { DateTimeInput } from "@/components/inputs/DateTimeInput";
import { Button } from "@/components/ui/button";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";

import { GameInput, zGameInput } from "./validation/game";

export const GameForm = ({
    onSubmit,
    defaultValues,
    onDelete,
}: {
    onSubmit: SubmitHandler<GameInput>;
    defaultValues: GameInput;
    onDelete?: () => void;
}) => {
    const form = useForm<GameInput>({
        defaultValues: defaultValues,
        mode: "onBlur",
        resolver: zodResolver(zGameInput),
    });

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                <FormField
                    control={form.control}
                    name="player1Color"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Player 1 color</FormLabel>
                            <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value ?? undefined}
                            >
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a player color" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    <SelectItem value="WHITE">White</SelectItem>
                                    <SelectItem value="BLACK">Black</SelectItem>
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="player2Color"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Player 2 color</FormLabel>
                            <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value ?? undefined}
                            >
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a player color" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    <SelectItem value="WHITE">White</SelectItem>
                                    <SelectItem value="BLACK">Black</SelectItem>
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="startingPositionFen"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Starting position FEN</FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="Starting position FEN"
                                    {...field}
                                    value={field.value ?? ""}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="scheduledStartAt"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Scheduled start at</FormLabel>
                            <FormControl>
                                <DateTimeInput value={field.value} onValue={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="startedAt"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Started at</FormLabel>
                            <FormControl>
                                <DateTimeInput value={field.value} onValue={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="finishedAt"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Finished at</FormLabel>
                            <FormControl>
                                <DateTimeInput value={field.value} onValue={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="result"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>Result</FormLabel>
                            <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value ?? undefined}
                            >
                                <FormControl>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select a result" />
                                    </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                    <SelectItem value="WHITE">White</SelectItem>
                                    <SelectItem value="BLACK">Black</SelectItem>
                                    <SelectItem value="DRAW">Draw</SelectItem>
                                    <SelectItem value="ABORTED">Aborted</SelectItem>
                                </SelectContent>
                            </Select>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <FormField
                    control={form.control}
                    name="pgnRoundTag"
                    render={({ field }) => (
                        <FormItem>
                            <FormLabel>
                                PGN round tag (don't set this if you don't know what you do)
                            </FormLabel>
                            <FormControl>
                                <Input
                                    placeholder="PGN round tag"
                                    {...field}
                                    value={field.value ?? ""}
                                />
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />

                <div className="mt-4 flex justify-between gap-2">
                    <Button
                        type="submit"
                        disabled={!form.formState.isValid || form.formState.isSubmitting}
                    >
                        Save
                    </Button>
                    {onDelete && (
                        <Button
                            type="button"
                            variant="outline"
                            className="border-red-600 text-red-600"
                            onClick={onDelete}
                        >
                            Delete game
                        </Button>
                    )}
                </div>
            </form>
        </Form>
    );
};
